import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Spin} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AppContextContext, OrganizationServiceContext, SbomProjectGroupServiceContext, SbomProjectServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import SbomProject from "../../domain/SbomProject";
import SbomProjectGroup from "../../domain/SbomProjectGroup";
import {Permission} from "../../domain/auth/Permission";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {definedValues} from "../../utils/SelectOptionUtils";
import {matchesSearchText} from "../../utils/StringUtils";
import * as globalStyles from "../App.module.css";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";
import {DocumentTitle} from "../DocumentTitle";
import {SbomProjectTable} from "../sbomproject/SbomProjectList";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function SbomProjectGroupDetail() {
    const appContext = useContext(AppContextContext);
    const sbomProjectGroupService = useContext(SbomProjectGroupServiceContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const navigate = useNavigate();
    const {sbomProjectGroupId}: any = useParams();
    const [editMode, setEditMode] = useState(false);
    const [editForm] = useForm();
    const selectedOrganizationId = Form.useWatch("organization", editForm);
    const [group, setGroup] = useState<SbomProjectGroup>();
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [availableProjects, setAvailableProjects] = useState<SbomProject[]>();
    const hasManageOrg = appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG);
    const userIsSystemAdmin = appContext.user.isSystemAdmin();
    const projectOrganizationId = userIsSystemAdmin ? selectedOrganizationId ?? group?.organization?.id : undefined;

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    const reload = useCallback(() => {
        sbomProjectGroupService.get(sbomProjectGroupId).then(group => {
            setGroup(group);

            editForm.setFieldsValue({
                ...group,
                organization: group.organization?.id,
                projectIds: group.projects?.map(project => project.id)
            });
        });
    }, [editForm, sbomProjectGroupId, sbomProjectGroupService]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (!userIsSystemAdmin) {
            return;
        }

        organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(value => setOrganizations(value.data));
    }, [organizationService, userIsSystemAdmin]);

    useEffect(() => {
        if (!hasManageOrg) {
            return;
        }

        if (userIsSystemAdmin && !projectOrganizationId) {
            setAvailableProjects(undefined);

            return;
        }

        sbomProjectService.getListForOrganizationScope(projectOrganizationId)
            .then(value => setAvailableProjects(value.data));
    }, [hasManageOrg, projectOrganizationId, sbomProjectService, userIsSystemAdmin]);

    return (
        <DocumentTitle title={group?.name}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/sbom-project-groups"}>Project groups</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{group?.name}</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={!group} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{group?.name}</h1>

                    {hasManageOrg &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                            <Button danger={true}
                                    title={"Delete"}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this project group?",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDeleteConfirm()
                                        });
                                    }}>Delete</Button>
                        </div>
                    }

                    {hasManageOrg && renderEditForm()}

                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                        <Descriptions.Item label={"Description"}>{group?.description || <i>no description</i>}</Descriptions.Item>

                        {userIsSystemAdmin && group?.organization &&
                            <Descriptions.Item label={"Organization"}>
                                <Link to={`/organizations/${group.organization.id}`}>{group.organization.name}</Link>
                            </Descriptions.Item>
                        }
                    </Descriptions>

                    {group &&
                        <>
                            <h2>Projects</h2>

                            <SbomProjectTable key={projectTableKey(group)} fixedGroupId={group.id}/>
                        </>
                    }
                </Spin>
            </>
        </DocumentTitle>
    );

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of project group {group?.name}</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."},
                            {
                                validator: serverViolationsHolder.createServerValidator(
                                    'UNIQUE',
                                    'name, organization_id',
                                    undefined,
                                    {compareLowerCaseValues: true}
                                ),
                                message: "Name is already used."
                            }
                        ]}>
                        <Input autoFocus={true} maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"description"}
                        label={"Description"}>
                        <Input.TextArea maxLength={1000} rows={4}/>
                    </Form.Item>

                    {userIsSystemAdmin &&
                        <Form.Item
                            name={"organization"}
                            label={"Organization"}
                            rules={[{required: true, message: "Organization is required."}]}>
                            <Select onChange={() => editForm.setFieldValue("projectIds", [])}>
                                {organizations?.map(organization =>
                                    <Select.Option key={organization.id} value={organization.id}>{organization.name}</Select.Option>
                                )}
                            </Select>
                        </Form.Item>
                    }

                    <Form.Item
                        name={"projectIds"}
                        label={"Projects"}>
                        <SelectAllMultiSelect
                            selectAllValues={definedValues(availableProjects, project => project.id)}
                            showSearch={true}
                            filterOption={(inputValue, option) => matchesSearchText(inputValue, option?.label)}
                            placeholder={"no projects"}>
                            {availableProjects?.map(project =>
                                <Select.Option key={project.id} value={project.id} label={project.name}>{project.name}</Select.Option>
                            )}
                        </SelectAllMultiSelect>
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        );
    }

    function onFinishEdit(values: any) {
        sbomProjectGroupService.update(sbomProjectGroupId, values)
            .then(
                () => {
                    message.success(<>Project group <b>{values.name}</b> successfully updated.</>);

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm() {
        if (!group) {
            return;
        }

        sbomProjectGroupService.delete(group)
            .then(() => {
                message.success(<>Project group <b>{group.name}</b> successfully deleted.</>);

                navigate(`/sbom-project-groups`);
            });
    }
}

function projectTableKey(group: SbomProjectGroup) {
    return [
        group.id,
        ...(group.projects ?? []).map(project => project.id).sort()
    ].join(":");
}

export default SbomProjectGroupDetail;
